import { useEffect, useRef, useState } from 'react';

interface UseCountUpOptions {
  /** Final value to animate to. */
  readonly target: number;
  /** Animation duration in milliseconds. Defaults to 1400ms (matches prototype). */
  readonly durationMs?: number;
  /** Only start animating once this fraction of the element is visible. */
  readonly threshold?: number;
}

/**
 * Animate a number from 0 to `target` using requestAnimationFrame.
 * The animation only fires when the returned ref enters the viewport — so the
 * counter doesn't tick away while the user is still scrolling toward it.
 *
 * Returns `[ref, currentValue]`. Attach the ref to the element you want to
 * observe (typically the parent container of the displayed number).
 *
 * Mirrors the static prototype's IntersectionObserver-driven hero stats.
 * Honors prefers-reduced-motion: jumps straight to the target.
 */
export function useCountUp({
  target,
  durationMs = 1400,
  threshold = 0.4,
}: UseCountUpOptions): [React.RefObject<HTMLElement>, number] {
  const ref = useRef<HTMLElement>(null);
  const [value, setValue] = useState<number>(0);

  useEffect(() => {
    const node = ref.current;
    if (!node || typeof window === 'undefined') return;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) {
      setValue(target);
      return;
    }

    let rafId = 0;
    const start = performance.now();
    const tick = (now: number) => {
      const progress = Math.min(1, (now - start) / durationMs);
      // Ease-out cubic — fast at start, gentle landing.
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.round(target * eased));
      if (progress < 1) rafId = requestAnimationFrame(tick);
    };

    let started = false;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!started && entry.isIntersecting) {
            started = true;
            rafId = requestAnimationFrame(tick);
          }
        });
      },
      { threshold },
    );
    observer.observe(node);

    return () => {
      observer.disconnect();
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [target, durationMs, threshold]);

  return [ref, value];
}
